// src/components/pages/Library/BookMaster.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Popover,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { styled, useTheme } from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import BulkAddIcon from '@mui/icons-material/PlaylistAdd';

import axiosInstance from '../../../WaralsAdmin/src/utils/axiosInstance';
import { decimalOnly } from '../../utils/numericInput';
import { useBranch } from './../../layouts/BranchContext';

// =========================================================
// SHARED SCROLLBAR STYLES
// =========================================================

const scrollbarStyles = (theme) => {
  const dark = theme.palette.mode === 'dark';

  const thumb = dark
    ? 'rgba(255,255,255,0.25)'
    : 'rgba(0,0,0,0.22)';

  return {
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,  
    scrollbarWidth: 'thin',
  };
};

const CustomTableScrollbar = styled('div')(
  ({ theme }) => ({
    overflowY: 'auto',
    backgroundColor:
      theme.palette.background.paper,
    ...scrollbarStyles(theme),
  })
);

// =========================================================
// INITIAL FORM
// =========================================================

const initialForm = {
  isbn: '',
  title: '',
  subtitle: '',
  category: '',
  subject: '',
  author: '',
  publisher: '',
  edition: '',
  language: '',
  course: '',
  batch: '',
  total_copies: 0,
  available_copies: 0,
  shelf_no: '',
  price: '',
  purchase_date: '',
  status: 'active',
};

// =========================================================
// BULK ADD CONFIG
// =========================================================

const EMPTY_BULK_ROW = {
  title: '',
  author: '',
  category: '',
  isbn: '',
  publisher: '',
  edition: '',
  language: '',
  shelf_no: '',
  price: '',
  total_copies: 1,
  available_copies: 1,
};

const BULK_FIELDS = [
  { key: 'title', label: 'Title *', max: 250 },
  { key: 'author', label: 'Author *', max: 200 },
  { key: 'category', label: 'Category *', select: true },
  { key: 'isbn', label: 'ISBN', max: 20 },
  { key: 'publisher', label: 'Publisher', max: 200 },
  { key: 'edition', label: 'Edition', max: 100 },
  { key: 'language', label: 'Language', max: 100 },
  { key: 'shelf_no', label: 'Shelf No', max: 100 },
  { key: 'price', label: 'Price' },
  { key: 'total_copies', label: 'Total Copies *', type: 'number' },
  { key: 'available_copies', label: 'Available Copies *', type: 'number', disabled: true },
];

// =========================================================
// HELPERS
// =========================================================

const getSessionUser = () => {
  try {
    const storedUser =
      sessionStorage.getItem('user') ||
      sessionStorage.getItem('userData') ||
      sessionStorage.getItem('currentUser');

    return storedUser ? JSON.parse(storedUser) : {};
  } catch (error) {
    console.error('Failed to parse session user:', error);
    return {};
  }
};

const extractArray = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  if (Array.isArray(responseData?.data?.results)) {
    return responseData.data.results;
  }

  if (Array.isArray(responseData?.data?.data)) {
    return responseData.data.data;
  }

  if (Array.isArray(responseData?.books)) {
    return responseData.books;
  }

  if (Array.isArray(responseData?.book_list)) {
    return responseData.book_list;
  }

  if (Array.isArray(responseData?.records)) {
    return responseData.records;
  }

  return [];
};

const getErrorMessage = (
  error,
  fallback = 'Something went wrong'
) => {
  const data = error?.response?.data;

  if (!data) {
    return error?.message || fallback;
  }

  if (typeof data === 'string') {
    return data;
  }

  if (data.detail) {
    return String(data.detail);
  }

  if (data.message) {
    return String(data.message);
  }

  if (data.error) {
    if (typeof data.error === 'string') {
      return data.error;
    }

    if (typeof data.error === 'object') {
      return getErrorMessage(
        { response: { data: data.error } },
        fallback
      );
    }
  }

  if (data.errors) {
    const errors = data.errors;

    if (typeof errors === 'string') {
      return errors;
    }

    if (typeof errors === 'object') {
      const messages = [];

      Object.entries(errors).forEach(([field, value]) => {
        if (Array.isArray(value)) {
          value.forEach((message) => {
            if (typeof message === 'string') {
              messages.push(
                field === 'non_field_errors'
                  ? message
                  : `${field}: ${message}`
              );
            } else if (
              message &&
              typeof message === 'object'
            ) {
              messages.push(
                `${field}: ${JSON.stringify(message)}`
              );
            }
          });
        } else if (typeof value === 'string') {
          messages.push(`${field}: ${value}`);
        } else if (
          value &&
          typeof value === 'object'
        ) {
          messages.push(
            `${field}: ${JSON.stringify(value)}`
          );
        }
      });

      if (messages.length) {
        return messages.join(' | ');
      }
    }
  }

  if (typeof data === 'object') {
    const messages = [];

    Object.entries(data).forEach(([field, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === 'string') {
            messages.push(`${field}: ${item}`);
          }
        });
      } else if (typeof value === 'string') {
        messages.push(`${field}: ${value}`);
      }
    });

    if (messages.length) {
      return messages.join(' | ');
    }

    try {
      return JSON.stringify(data);
    } catch {
      return fallback;
    }
  }

  return fallback;
};

const nullable = (v) => {
  const s = String(v ?? '').trim();
  return s === '' ? null : s;
};

const PRICE_REGEX = /^\d+(\.\d{1,2})?$/;

// =========================================================
// COMPONENT
// =========================================================

function BookMaster() {
  const theme = useTheme();

  // Global branch context (navbar)
  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === 'dark';

  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.disabled;
  const texterror = theme.palette.text.error;

  const paperBackground = theme.palette.background.paper;

  const inputBackground = isDark
    ? 'rgba(255,255,255,0.06)'
    : '#ffffff';

  const tableRowHover = isDark
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(0,0,0,0.025)';

  // =======================================================
  // SESSION
  // =======================================================

  const organizationId = useMemo(() => {
    try {
      return (
        JSON.parse(
          sessionStorage.getItem('user') || '{}'
        ).organization_id || ''
      );
    } catch {
      return '';
    }
  }, []);

  // =======================================================
  // STATES
  // =======================================================

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [activeFilter, setActiveFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Filter popover draft values; applied only after clicking Apply.
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [draftActiveFilter, setDraftActiveFilter] = useState('all');
  const [draftConditionFilter, setDraftConditionFilter] = useState('all');
  const [draftCategoryFilter, setDraftCategoryFilter] = useState('all');
  const filterPopoverOpen = Boolean(filterAnchorEl);

  const openFilterPopover = (event) => {
    setDraftActiveFilter(activeFilter);
    setDraftConditionFilter(conditionFilter);
    setDraftCategoryFilter(categoryFilter);
    setFilterAnchorEl(event.currentTarget);
  };

  const closeFilterPopover = () => setFilterAnchorEl(null);

  const applyFilters = () => {
    setActiveFilter(draftActiveFilter);
    setConditionFilter(draftConditionFilter);
    setCategoryFilter(draftCategoryFilter);
    setPage(1);
    closeFilterPopover();
  };

  const resetFilters = () => {
    setDraftActiveFilter('all');
    setDraftConditionFilter('all');
    setDraftCategoryFilter('all');
    setActiveFilter('all');
    setConditionFilter('all');
    setCategoryFilter('all');
    setPage(1);
    closeFilterPopover();
  };

  const [ordering, setOrdering] = useState('title');
  const [saving, setSaving] = useState(false);
  const requestId = useRef(0);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const rowsPerPageOptions = [5, 10, 25, 50];

  // =======================================================
  // DROPDOWNS
  // =======================================================

  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  // =======================================================
  // MODALS
  // =======================================================

  const [showAddBookModal, setShowAddBookModal] =
    useState(false);

  const [showEditBookModal, setShowEditBookModal] =
    useState(false);

  const [showDeleteModal, setShowDeleteModal] =
    useState(false);

  const [showBulkModal, setShowBulkModal] =
    useState(false);

  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  const [selectedBook, setSelectedBook] =
    useState(null);

  const [statusBook, setStatusBook] = useState(null);

  const [bulkRows, setBulkRows] = useState([
    { ...EMPTY_BULK_ROW },
  ]);

  // =======================================================
  // FORM
  // =======================================================

  const [formData, setFormData] =
    useState(initialForm);

  // =======================================================
  // SNACKBAR
  // =======================================================

  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState('');

  const [snackbarSeverity, setSnackbarSeverity] =
    useState('success');

  const showSnackbar = useCallback(
    (message, severity = 'success') => {
      setSnackbarMessage(
        String(message || 'Something went wrong')
      );

      setSnackbarSeverity(severity);
      setOpenSnackbar(true);
    },
    []
  );

  // =======================================================
  // COMMON INPUT STYLE
  // =======================================================

  const commonInputSx = {
    mb: 2,

    '& .MuiInputBase-root': {
      color: textPrimary,
      backgroundColor: inputBackground,
    },

    '& .MuiInputBase-input': {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },

    '& .MuiInputBase-input::placeholder': {
      color: textSecondary,
      opacity: 0.85,
    },

    '& .MuiInputLabel-root': {
      color: textPrimary,
    },

    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark
        ? 'rgba(255,255,255,0.30)'
        : 'rgba(0,0,0,0.25)',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },

    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
      {
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
      },

    // Dark-mode date picker icon
    '& input[type="date"]': {
      colorScheme: isDark ? 'dark' : 'light',
    },
  };

  // =======================================================
  // BOOK LIST API
  // =======================================================

  const fetchBooks = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setBooks([]);
      return;
    }

    const currentRequest = ++requestId.current;

    try {
      setLoading(true);

      const params = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (activeFilter !== 'all') {
        params.is_active = activeFilter;
      }

      if (conditionFilter !== 'all') {
        params.status = conditionFilter;
      }

      if (categoryFilter !== 'all') {
        params.category = Number(categoryFilter);
      }

      const response = await axiosInstance.get(
        '/library/book-master/list/',
        { params }
      );

      if (currentRequest !== requestId.current) {
        return;
      }

      setBooks(extractArray(response?.data));
    } catch (error) {
      if (currentRequest !== requestId.current) {
        return;
      }

      console.error('Fetch books error:', error);

      showSnackbar(
        getErrorMessage(error, 'Failed to fetch books'),
        'error'
      );

      setBooks([]);
    } finally {
      if (currentRequest === requestId.current) {
        setLoading(false);
      }
    }
  }, [
    organizationId,
    selectedBranchId,
    debouncedSearch,
    activeFilter,
    conditionFilter,
    categoryFilter,
    showSnackbar,
  ]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      400
    );

    return () => clearTimeout(t);
  }, [searchTerm]);

  // Back to page 1 only when filters change
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    activeFilter,
    conditionFilter,
    categoryFilter,
    rowsPerPage,
  ]);

  // =======================================================
  // CATEGORY API
  // =======================================================

  const fetchBookCategories =
    useCallback(async () => {
      if (!organizationId || !selectedBranchId) {
        return;
      }

      try {
        const response =
          await axiosInstance.get(
            '/library/book-category/dropdown/',
            {
              params: {
                organization: Number(organizationId),
                branch: Number(selectedBranchId),
              },
            }
          );

        setCategories(
          extractArray(response?.data)
        );
      } catch (error) {
        console.error(
          'Fetch book categories error:',
          error
        );

        showSnackbar(
          getErrorMessage(
            error,
            'Failed to load book categories'
          ),
          'error'
        );

        setCategories([]);
      }
    }, [
      organizationId,
      selectedBranchId,
      showSnackbar,
    ]);

  // =======================================================
  // COURSE API
  // =======================================================

  const fetchCourses = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      return;
    }

    try {
      const response =
        await axiosInstance.get(
          '/course/courses/',
          {
            params: {
              organization_id: Number(organizationId),
              branch_id: Number(selectedBranchId),
            },
          }
        );

      const courseList = extractArray(
        response?.data
      );

      const filteredCourses = courseList.filter(
        (course) => {
          if (course?.is_active === false) {
            return false;
          }

          const courseOrganizationId =
            course?.organization?.id ??
            course?.organization_id ??
            course?.organizationId ??
            course?.organization;

          const courseBranchId =
            course?.branch?.id ??
            course?.branch_id ??
            course?.branchId ??
            course?.branch;

          return (
            String(courseOrganizationId) ===
              String(organizationId) &&
            String(courseBranchId) ===
              String(selectedBranchId)
          );
        }
      );

      setCourses(filteredCourses);
    } catch (error) {
      console.error('Fetch courses error:', error);

      showSnackbar(
        getErrorMessage(
          error,
          'Failed to load courses'
        ),
        'error'
      );

      setCourses([]);
    }
  }, [
    organizationId,
    selectedBranchId,
    showSnackbar,
  ]);

  // =======================================================
  // BATCH API
  // =======================================================

  const fetchBatches = useCallback(async () => {
    if (
      !organizationId ||
      !selectedBranchId ||
      !formData.course
    ) {
      return;
    }

    try {
      const response =
        await axiosInstance.get(
          '/course/batches/',
          {
            params: {
              organization_id: Number(organizationId),
              branch_id: Number(selectedBranchId),
              course_id: Number(formData.course),
            },
          }
        );

      const batchList = extractArray(
        response?.data
      );

      const filteredBatches = batchList.filter(
        (batch) => {
          if (batch?.is_active === false) {
            return false;
          }

          const batchBranchId =
            batch?.branch?.id ??
            batch?.branch_id ??
            batch?.branchId ??
            batch?.branch;

          const batchCourseId =
            batch?.course?.id ??
            batch?.course_id ??
            batch?.courseId ??
            batch?.course;

          return (
            batchBranchId == null ||
            String(batchBranchId) ===
              String(selectedBranchId)
          ) && (
            batchCourseId == null ||
            String(batchCourseId) ===
              String(formData.course)
          );
        }
      );

      setBatches(filteredBatches);
    } catch (error) {
      console.error('Fetch batches error:', error);

      showSnackbar(
        getErrorMessage(
          error,
          'Failed to load batches'
        ),
        'error'
      );

      setBatches([]);
    }
  }, [
    organizationId,
    selectedBranchId,
    formData.course,
    showSnackbar,
  ]);

  // =======================================================
  // DROPDOWNS ON BRANCH CHANGE
  // =======================================================

  useEffect(() => {
    if (!organizationId || !selectedBranchId) {
      return;
    }

    fetchBookCategories();
    fetchCourses();
  }, [
    organizationId,
    selectedBranchId,
    fetchBookCategories,
    fetchCourses,
  ]);

  // =======================================================
  // BATCHES ON COURSE CHANGE
  // =======================================================

  useEffect(() => {
    if (
      !organizationId ||
      !selectedBranchId ||
      !formData.course
    ) {
      setBatches([]);
      return;
    }

    fetchBatches();
  }, [
    organizationId,
    selectedBranchId,
    formData.course,
    fetchBatches,
  ]);

  // =======================================================
  // RESET ON NAVBAR BRANCH CHANGE
  // =======================================================

  useEffect(() => {
    setPage(1);
    setCategoryFilter('all');
    setCategories([]);
    setCourses([]);
    setBatches([]);

    setFormData((prev) => ({
      ...prev,
      category: '',
      course: '',
      batch: '',
    }));
  }, [selectedBranchId]);

  // =======================================================
  // FORM CHANGE
  // =======================================================

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      if (field === 'total_copies') {
        const previousTotal = Number(prev.total_copies) || 0;
        const nextTotal = value === '' ? 0 : Number(value) || 0;
        const previousAvailable = Number(prev.available_copies) || 0;

        // Adjust available copies by the same change in total copies.
        // This preserves the number of copies already issued in edit mode.
        const issuedCopies = Math.max(0, previousTotal - previousAvailable);
        const nextAvailable = Math.max(0, nextTotal - issuedCopies);

        return {
          ...prev,
          total_copies: value,
          available_copies: String(nextAvailable),
        };
      }

      if (field === 'available_copies') return prev;

      return {
        ...prev,
        [field]: value,
        // Clear the batch whenever the course changes
        ...(field === 'course' ? { batch: '' } : {}),
      };
    });
  };

  // =======================================================
  // VALIDATION & PAYLOAD
  // =======================================================

  const validateBookForm = () => {
    if (!organizationId) {
      return 'Organization not found in session';
    }

    if (!selectedBranchId) {
      return 'Please select a branch from the navbar';
    }

    if (!formData.title.trim()) {
      return 'Book title is required';
    }

    if (!formData.category) {
      return 'Category is required';
    }

    if (!formData.author.trim()) {
      return 'Author is required';
    }

    const total = Number(formData.total_copies);
    const available = Number(
      formData.available_copies
    );

    if (
      !Number.isInteger(total) ||
      !Number.isInteger(available) ||
      total < 0 ||
      available < 0
    ) {
      return 'Copies must be whole numbers, 0 or more';
    }

    if (available > total) {
      return 'Available copies cannot be greater than total copies';
    }

    if (
      String(formData.price).trim() !== '' &&
      !PRICE_REGEX.test(
        String(formData.price).trim()
      )
    ) {
      return 'Price must be a valid amount, e.g. 250 or 250.50';
    }

    return null;
  };

  const buildBookPayload = () => {
    const payload = {
      organization: Number(organizationId),
      branch: Number(selectedBranchId),
      isbn: nullable(formData.isbn),
      title: formData.title.trim(),
      subtitle: nullable(formData.subtitle),
      category: Number(formData.category),
      subject: nullable(formData.subject),
      author: formData.author.trim(),
      publisher: nullable(formData.publisher),
      edition: nullable(formData.edition),
      language: nullable(formData.language),
      course: formData.course
        ? Number(formData.course)
        : null,
      batch: formData.batch
        ? Number(formData.batch)
        : null,
      total_copies: Number(formData.total_copies),
      available_copies: Number(
        formData.available_copies
      ),
      shelf_no: nullable(formData.shelf_no),
      purchase_date: formData.purchase_date || null,
      status: formData.status,
    };

    if (String(formData.price).trim() !== '') {
      payload.price = String(formData.price).trim();
    }

    return payload;
  };

  // =======================================================
  // ADD
  // =======================================================

  const handleAddBook = () => {
    setFormData({ ...initialForm });
    setShowAddBookModal(true);
  };

  // =======================================================
  // CREATE BOOK
  // =======================================================

  const handleSaveBook = async () => {
    const validationError = validateBookForm();

    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    try {
      setSaving(true);

      const response =
        await axiosInstance.post(
          '/library/book-master/create/',
          buildBookPayload()
        );

      // API restores a matching inactive book
      // instead of duplicating it
      showSnackbar(
        response?.data?.message ||
          'Book saved successfully'
      );

      setShowAddBookModal(false);
      setFormData({ ...initialForm });

      await fetchBooks();
    } catch (error) {
      showSnackbar(
        getErrorMessage(
          error,
          'Failed to create book'
        ),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // =======================================================
  // EDIT
  // =======================================================

  const handleEdit = (book) => {
    setSelectedBook(book);

    setFormData({
      isbn: book?.isbn || '',
      title: book?.title || '',
      subtitle: book?.subtitle || '',
      category:
        book?.category_id ??
        book?.category ??
        '',
      subject: book?.subject || '',
      author: book?.author || '',
      publisher: book?.publisher || '',
      edition: book?.edition || '',
      language: book?.language || '',
      course: book?.course_id ?? book?.course ?? '',
      batch: book?.batch_id ?? book?.batch ?? '',
      total_copies: book?.total_copies ?? 0,
      available_copies:
        book?.available_copies ?? 0,
      shelf_no: book?.shelf_no || '',
      price: book?.price || '',
      purchase_date: book?.purchase_date || '',
      status: book?.status || 'active',
    });

    setShowEditBookModal(true);
  };

  // =======================================================
  // UPDATE BOOK
  // =======================================================

  const handleUpdateBook = async () => {
    if (!selectedBook?.id) {
      showSnackbar('Book ID not found', 'error');
      return;
    }

    const validationError = validateBookForm();

    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    try {
      setSaving(true);

      await axiosInstance.put(
        `/library/book-master/update/${selectedBook.id}/`,
        {
          ...buildBookPayload(),
          is_active:
            selectedBook?.is_active ?? true,
        }
      );

      showSnackbar('Book updated successfully');

      setShowEditBookModal(false);
      setSelectedBook(null);
      setFormData({ ...initialForm });

      await fetchBooks();
    } catch (error) {
      showSnackbar(
        getErrorMessage(
          error,
          'Failed to update book'
        ),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // =======================================================
  // ACTIVATE / DEACTIVATE (SWITCH)
  // =======================================================

  const handleConfirmStatus = async () => {
    if (!statusBook?.id) {
      return;
    }

    const nextActive = !statusBook.is_active;

    try {
      setSaving(true);

      await axiosInstance.patch(
        `/library/book-master/status/${statusBook.id}/`,
        { is_active: nextActive }
      );

      showSnackbar(
        `Book ${nextActive ? 'activated' : 'deactivated'} successfully`
      );

      setStatusBook(null);

      await fetchBooks();
    } catch (error) {
      showSnackbar(
        getErrorMessage(
          error,
          'Failed to update status'
        ),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // =======================================================
  // DELETE (SOFT DELETE)
  // =======================================================

  const handleShowDeleteModal = (book) => {
    setSelectedBook(book);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedBook(null);
  };

  const handleDelete = async () => {
    try {
      if (!selectedBook?.id) {
        showSnackbar('Book ID not found', 'error');
        return;
      }

      await axiosInstance.delete(
        `/library/book-master/delete/${selectedBook.id}/`
      );

      showSnackbar('Book deleted successfully');

      handleCloseDeleteModal();

      await fetchBooks();
    } catch (error) {
      console.error('BOOK DELETE ERROR:', error);

      showSnackbar(
        getErrorMessage(
          error,
          'Failed to delete book'
        ),
        'error'
      );
    }
  };

  // =======================================================
  // BULK ADD
  // =======================================================

  const openBulkModal = () => {
    setBulkRows([{ ...EMPTY_BULK_ROW }]);
    setShowBulkModal(true);
  };

  const updateBulkRow = (i, field, value) =>
    setBulkRows((rows) =>
      rows.map((r, idx) => {
        if (idx !== i) return r;

        // For a new book, all copies are available initially.
        // Keep Available Copies synced with Total Copies and read-only.
        if (field === 'total_copies') {
          return {
            ...r,
            total_copies: value,
            available_copies: value === '' ? '' : value,
          };
        }

        if (field === 'available_copies') return r;
        return {
          ...r,
          [field]: field === 'price' ? decimalOnly(value) : value,
        };
      })
    );

  const addBulkRow = () =>
    setBulkRows((rows) => [
      ...rows,
      { ...EMPTY_BULK_ROW },
    ]);

  const removeBulkRow = (i) =>
    setBulkRows((rows) =>
      rows.length === 1
        ? rows
        : rows.filter((_, idx) => idx !== i)
    );

  const handleBulkSave = async () => {
    if (!organizationId) {
      showSnackbar(
        'Organization not found in session',
        'error'
      );
      return;
    }

    if (!selectedBranchId) {
      showSnackbar(
        'Please select a branch from the navbar',
        'error'
      );
      return;
    }

    for (let i = 0; i < bulkRows.length; i++) {
      const r = bulkRows[i];
      const n = i + 1;

      if (!r.title.trim() || !r.author.trim() || !r.category) {
        showSnackbar(
          `Book ${n}: Title, Author and Category are required`,
          'error'
        );
        return;
      }

      const total = Number(r.total_copies);
      const available = Number(r.available_copies);

      if (
        !Number.isInteger(total) ||
        !Number.isInteger(available) ||
        total < 0 ||
        available < 0
      ) {
        showSnackbar(
          `Book ${n}: copies must be whole numbers, 0 or more`,
          'error'
        );
        return;
      }

      if (available > total) {
        showSnackbar(
          `Book ${n}: available copies cannot exceed total copies`,
          'error'
        );
        return;
      }

      if (
        String(r.price).trim() !== '' &&
        !PRICE_REGEX.test(String(r.price).trim())
      ) {
        showSnackbar(
          `Book ${n}: invalid price`,
          'error'
        );
        return;
      }
    }

    // Same Title + Author + Edition twice in one
    // request is a duplicate
    const keys = bulkRows.map((r) =>
      [r.title, r.author, r.edition]
        .map((v) => v.trim().toLowerCase())
        .join('|')
    );

    if (new Set(keys).size !== keys.length) {
      showSnackbar(
        'Duplicate books (same Title + Author + Edition) found in the list',
        'error'
      );
      return;
    }

    const payload = {
      organization: Number(organizationId),
      branch: Number(selectedBranchId),
      books: bulkRows.map((r) => {
        const book = {
          isbn: nullable(r.isbn),
          title: r.title.trim(),
          category: Number(r.category),
          author: r.author.trim(),
          publisher: nullable(r.publisher),
          edition: nullable(r.edition),
          language: nullable(r.language),
          shelf_no: nullable(r.shelf_no),
          total_copies: Number(r.total_copies),
          available_copies: Number(
            r.available_copies
          ),
          status: 'active',
        };

        if (String(r.price).trim() !== '') {
          book.price = String(r.price).trim();
        }

        return book;
      }),
    };

    try {
      setSaving(true);

      const response = await axiosInstance.post(
        '/library/book-master/bulk-create/',
        payload
      );

      const result = response?.data;

      console.log('Bulk create response:', result);

      const count = (v) =>
        Array.isArray(v)
          ? v.length
          : Number(v) || 0;

      const summary = result?.summary || result;

      showSnackbar(
        result?.message ||
          `Created: ${count(summary?.created)}, Restored: ${count(summary?.restored)}, Skipped: ${count(summary?.skipped)}`
      );

      setShowBulkModal(false);

      await fetchBooks();
    } catch (error) {
      showSnackbar(
        getErrorMessage(
          error,
          'Failed to bulk create books'
        ),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // =======================================================
  // SORTING & PAGINATION
  // =======================================================

  const handleSort = (field) =>
    setOrdering((prev) =>
      prev === field ? `-${field}` : field
    );

  const sortedBooks = useMemo(() => {
    const desc = ordering.startsWith('-');
    const key = ordering.replace('-', '');

    return [...books].sort((a, b) => {
      const av = String(a?.[key] ?? '');
      const bv = String(b?.[key] ?? '');

      const cmp = av.localeCompare(bv, undefined, {
        numeric: true,
        sensitivity: 'base',
      });

      return desc ? -cmp : cmp;
    });
  }, [books, ordering]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedBooks.length / rowsPerPage)
  );

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedBooks.slice(
      start,
      start + rowsPerPage
    );
  }, [sortedBooks, page, rowsPerPage]);

  // If the last row on a page was removed, step back
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleChangePage = (
    _event,
    newPage
  ) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }

    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  // =======================================================
  // OPTIONS
  // =======================================================

  const categoryOptions = categories.map(
    (item) => ({
      value: item.id,
      label:
        item.name ||
        item.category_name ||
        item.title ||
        `Category ${item.id}`,
    })
  );

  const courseOptions = courses.map((item) => ({
    value: item.id,
    label:
      item.course_name ||
      item.name ||
      item.title ||
      item.course ||
      `Course ${item.id}`,
  }));

  const batchOptions = batches.map((item) => ({
    value: item.id,
    label:
      item.name ||
      item.batch_name ||
      item.title ||
      item.batch ||
      `Batch ${item.id}`,
  }));

  // =======================================================
  // MENU PROPS / FILTER PILLS
  // =======================================================

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: paperBackground,
        color: textPrimary,
        ...scrollbarStyles(theme),
        '& .MuiMenuItem-root': {
          color: textPrimary,
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor:
            theme.palette.action.hover,
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor:
            theme.palette.action.selected,
        },
      },
    },
  };

  const pillSelectSx = {
    height: 50,
    minWidth: 190,
    borderRadius: '30px',
    color: textPrimary,
    backgroundColor: inputBackground,

    '& .MuiSelect-select': {
      color: textPrimary,
      paddingLeft: '20px',
    },

    '& .MuiSvgIcon-root': {
      color: textPrimary,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark
        ? 'rgba(255,255,255,0.30)'
        : 'rgba(0,0,0,0.25)',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline':
      {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
  };

  const renderFilter = (
    label,
    value,
    onChange,
    options
  ) => (
    <Autocomplete
      size="small"
      options={options}
      value={
        options.find(
          (option) =>
            String(option.value) === String(value)
        ) || null
      }
      onChange={(_, option) =>
        onChange(option?.value ?? 'all')
      }
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        String(option.value) === String(selected.value)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{
            ...params.inputProps,
            'aria-label': label,
          }}
        />
      )}
      sx={{
        minWidth: 190,
        '& .MuiOutlinedInput-root': {
          height: 50,
          borderRadius: '30px',
          color: textPrimary,
          backgroundColor: inputBackground,
          paddingLeft: '20px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: isDark
            ? 'rgba(255,255,255,0.30)'
            : 'rgba(0,0,0,0.25)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
        '& .MuiSvgIcon-root': {
          color: textPrimary,
        },
        '& .MuiInputLabel-root': {
          color: textPrimary,
        },
      }}
    />
  );

  // =======================================================
  // SORTABLE HEADER
  // =======================================================

  const renderSortHeader = (label, field) => {
    const active =
      ordering.replace('-', '') === field;

    return (
      <TableCell
        sortDirection={
          active
            ? ordering.startsWith('-')
              ? 'desc'
              : 'asc'
            : false
        }
      >
        <TableSortLabel
          active={active}
          direction={
            ordering === `-${field}`
              ? 'desc'
              : 'asc'
          }
          onClick={() => handleSort(field)}
          sx={{
            color: `${theme.palette.primary.contrastText} !important`,

            '& .MuiTableSortLabel-icon': {
              color: `${theme.palette.primary.contrastText} !important`,
            },
          }}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  // =======================================================
  // TEXT FIELD
  // =======================================================

  const renderTextField = (
    label,
    field,
    options = {}
  ) => (
    <TextField
      label={label}
      fullWidth
      value={formData[field] ?? ''}
      onChange={(e) =>
        handleFormChange(
          field,
          field === 'price'
            ? decimalOnly(e.target.value)
            : e.target.value
        )
      }
      type={options.type || 'text'}
      multiline={options.multiline || false}
      rows={options.rows}
      inputProps={options.inputProps}
      sx={commonInputSx}
    />
  );

  // =======================================================
  // SELECT
  // =======================================================

  const renderSelect = (
    label,
    field,
    options = [],
    { disabled = false, clearable = false } = {}
  ) => (
    <Autocomplete
      fullWidth
      options={
        clearable
          ? [{ value: '', label: 'None' }, ...options]
          : options
      }
      value={
        (clearable
          ? [{ value: '', label: 'None' }, ...options]
          : options
        ).find(
          (option) =>
            String(option.value) ===
            String(formData[field] ?? '')
        ) || null
      }
      onChange={(_, option) =>
        handleFormChange(field, option?.value ?? '')
      }
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) =>
        String(option.value) === String(value.value)
      }
      disabled={disabled}
      noOptionsText={
        clearable ? 'No options available' : 'No options available'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={`Search ${label.toLowerCase()}...`}
          sx={{
            ...commonInputSx,
            '& .MuiInputLabel-root': {
              color: textPrimary,
              '&.Mui-focused': {
                color: theme.palette.primary.main,
              },
            },
            '& .MuiOutlinedInput-root': {
              backgroundColor: inputBackground,
            },
          }}
        />
      )}
      sx={{
        mb: 2,
        '& .MuiAutocomplete-inputRoot': {
          color: textPrimary,
          backgroundColor: inputBackground,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: isDark
            ? 'rgba(255,255,255,0.30)'
            : 'rgba(0,0,0,0.25)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
        '& .MuiSvgIcon-root': {
          color: textPrimary,
        },
      }}
    />
  );

  // =======================================================
  // BOOK FORM
  // =======================================================

  const renderBookForm = (isEdit = false) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(90vh - 64px)',
        backgroundColor: paperBackground,
      }}
    >
      {/* Scrollable fields */}

      <CustomTableScrollbar
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '28px 20px 8px',
        }}
      >
        {renderTextField('ISBN', 'isbn', {
          inputProps: { maxLength: 20 },
        })}

        {renderTextField('Title *', 'title', {
          inputProps: { maxLength: 250 },
        })}

        {renderTextField('Subtitle', 'subtitle', {
          inputProps: { maxLength: 250 },
        })}

        {renderSelect(
          'Category *',
          'category',
          categoryOptions
        )}

        {renderTextField('Subject', 'subject', {
          inputProps: { maxLength: 150 },
        })}

        {renderTextField('Author *', 'author', {
          inputProps: { maxLength: 200 },
        })}

        {renderTextField(
          'Publisher',
          'publisher',
          { inputProps: { maxLength: 200 } }
        )}

        {renderTextField('Edition', 'edition', {
          inputProps: { maxLength: 100 },
        })}

        {renderTextField('Language', 'language', {
          inputProps: { maxLength: 100 },
        })}

        {renderSelect(
          'Course',
          'course',
          courseOptions,
          { clearable: true }
        )}

        {renderSelect('Batch', 'batch', batchOptions, {
          clearable: true,
          disabled: !formData.course,
        })}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
            },
            gap: 2,
          }}
        >
          <TextField
            label="Total Copies *"
            type="number"
            fullWidth
            value={formData.total_copies}
            onChange={(e) =>
              handleFormChange(
                'total_copies',
                e.target.value
              )
            }
            inputProps={{ min: 0 }}
            sx={commonInputSx}
          />

          <TextField
            label="Available Copies *"
            type="number"
            fullWidth
            value={formData.available_copies}
            disabled
            inputProps={{ min: 0, readOnly: true }}
            sx={commonInputSx}
          />
        </Box>

        {renderTextField('Shelf No', 'shelf_no', {
          inputProps: { maxLength: 100 },
        })}

        {renderTextField('Price', 'price', {
          inputProps: {
            inputMode: 'decimal',
            maxLength: 12,
          },
        })}

        <TextField
          label="Purchase Date"
          type="date"
          fullWidth
          value={formData.purchase_date}
          onChange={(e) =>
            handleFormChange(
              'purchase_date',
              e.target.value
            )
          }
          InputLabelProps={{ shrink: true }}
          sx={commonInputSx}
        />

        {renderSelect('Status', 'status', [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'lost', label: 'Lost' },
          { value: 'damaged', label: 'Damaged' },
        ])}
      </CustomTableScrollbar>

      {/* Fixed footer */}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          p: 2,
          flexShrink: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: paperBackground,
        }}
      >
        <Button
          variant="contained"
          onClick={
            isEdit ? handleUpdateBook : handleSaveBook
          }
          disabled={
            saving ||
            !formData.title.trim() ||
            !formData.category ||
            !formData.author.trim()
          }
        >
          {saving ? (
            <CircularProgress
              size={22}
              color="inherit"
            />
          ) : isEdit ? (
            'UPDATE'
          ) : (
            'SAVE'
          )}
        </Button>

        <Button
          variant="contained"
          onClick={() => {
            if (isEdit) {
              setShowEditBookModal(false);
            } else {
              setShowAddBookModal(false);
            }
          }}
          disabled={saving}
        >
          CANCEL
        </Button>
      </Box>
    </Box>
  );

  // =======================================================
  // RETURN
  // =======================================================

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 2,
        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },
        color: textPrimary,
      }}
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            flex: 1,
          }}
        >
          {/* SEARCH */}

          <TextField
            variant="outlined"
            placeholder="Search by Book Code, ISBN, Title or Author"
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color:
                        theme.palette.primary.main,
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment:
                searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSearchTerm('')
                      }
                      sx={{
                        color: textSecondary,
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
            }}
            sx={{
              width: {
                xs: '100%',
                sm: 420,
              },

              '& .MuiOutlinedInput-root': {
                borderRadius: '30px',
                backgroundColor: inputBackground,
                height: 50,
                color: textPrimary,

                '& input': {
                  color: textPrimary,
                  WebkitTextFillColor: textPrimary,
                },

                '& input::placeholder': {
                  color: textSecondary,
                  opacity: 0.85,
                },

                '& .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: isDark
                      ? 'rgba(255,255,255,0.28)'
                      : 'rgba(0,0,0,0.22)',
                  },

                '&:hover .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor:
                      theme.palette.primary.main,
                  },

                '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                  {
                    borderWidth: 2,
                    borderColor:
                      theme.palette.primary.main,
                  },
              },
            }}
          />

          {/* FILTER BUTTON — keeps the search bar and action buttons unchanged */}
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={openFilterPopover}
            aria-controls={filterPopoverOpen ? 'book-filters-popover' : undefined}
            aria-haspopup="true"
            aria-expanded={filterPopoverOpen ? 'true' : undefined}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: '10px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
             color : theme.palette.primary.disabled
            }}
          >
            Filters
            {(activeFilter !== 'all' || conditionFilter !== 'all' || categoryFilter !== 'all') && (
              <Box component="span" sx={{ ml: 1, minWidth: 20, height: 20, px: 0.5, borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: '#fff', fontSize: 12 }}>
                {[activeFilter, conditionFilter, categoryFilter].filter((value) => value !== 'all').length}
              </Box>
            )}
          </Button>

          {/* ADD */}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBook}
            sx={{
              height: 50,
              px: 3,
              borderRadius: '10px',
              fontWeight: 'bold',
            }}
          >
            ADD BOOK
          </Button>

          {/* BULK ADD */}

          <Button
            variant="contained"
            onClick={openBulkModal}
            startIcon={<BulkAddIcon />}
            sx={{
              height: 50,
              px: 3,
              borderRadius: '10px',
              fontWeight: 'bold',
            }}
          >
            BULK ADD
          </Button>
        </Box>

        {/* GUIDE */}

        <IconButton
          onClick={() =>
            setShowUserGuideModal(true)
          }
          sx={{
            width: 55,
            height: 55,
            color: theme.palette.primary.main,
          }}
        >
          <InfoOutlineIcon
            sx={{ fontSize: 30 }}
          />
        </IconButton>
      </Box>

      <Popover
        id="book-filters-popover"
        open={filterPopoverOpen}
        anchorEl={filterAnchorEl}
        onClose={closeFilterPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2.5,
            width: { xs: 'calc(100vw - 32px)', sm: 340 },
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: 3,
            bgcolor: paperBackground,
            color: textPrimary,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Filter Books</Typography>
            <Typography variant="body2" sx={{ color: textSecondary }}>Choose filters, then apply your selection.</Typography>
          </Box>
          {renderFilter('Status', draftActiveFilter, setDraftActiveFilter, [
            { value: 'all', label: 'All' },
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' },
          ])}
          {renderFilter('Condition', draftConditionFilter, setDraftConditionFilter, [
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'lost', label: 'Lost' },
            { value: 'damaged', label: 'Damaged' },
          ])}
          {renderFilter('Category', draftCategoryFilter, setDraftCategoryFilter, [
            { value: 'all', label: 'All' },
            ...categoryOptions,
          ])}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, pt: 0.5 }}>
            <Button variant="text" onClick={resetFilters} sx={{ fontWeight: 600 }}>Reset</Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={closeFilterPopover} data-app-cancel="true">Cancel</Button>
              <Button variant="contained" onClick={applyFilters} sx={{ fontWeight: 600 }}>Apply</Button>
            </Box>
          </Box>
        </Stack>
      </Popover>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Paper
        elevation={3}
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: paperBackground,
          color: textPrimary,

          border: `1px solid ${
            isDark
              ? 'rgba(255,255,255,0.08)'
              : 'rgba(0,0,0,0.08)'
          }`,
        }}
      >
        <TableContainer component={CustomTableScrollbar}>
          <Table
            sx={{
              minWidth: 1300,

              '& .MuiTableCell-root': {
                color: textPrimary,
                borderColor: theme.palette.divider,
              },

              '& .MuiTableRow-hover:hover': {
                backgroundColor: tableRowHover,
              },

              '& .MuiTableHead-root .MuiTableCell-root':
                {
                  fontWeight: 'bold',
                  backgroundColor:
                    theme.palette.primary.main,
                  color:
                    theme.palette.primary
                      .contrastText,
                  whiteSpace: 'nowrap',
                },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>

                {renderSortHeader('Book Code', 'book_code')}
                {renderSortHeader('Title', 'title')}
                {renderSortHeader('Author', 'author')}

                <TableCell>ISBN</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Publisher</TableCell>
                <TableCell>Shelf No</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Available</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={13}
                    align="center"
                    sx={{
                      py: 5,
                      color: textPrimary,
                    }}
                  >
                    Loading books...
                  </TableCell>
                </TableRow>
              ) : paginatedData.length > 0 ? (
                paginatedData.map(
                  (book, index) => (
                    <TableRow
                      key={book.id}
                      hover
                    >
                      <TableCell>
                        {(page - 1) *
                          rowsPerPage +
                          index +
                          1}
                      </TableCell>

                      <TableCell
                        sx={{ fontWeight: 600 }}
                      >
                        {book.book_code || '-'}
                      </TableCell>

                      <TableCell>
                        {book.title || '-'}
                      </TableCell>

                      <TableCell>
                        {book.author || '-'}
                      </TableCell>

                      <TableCell>
                        {book.isbn || '-'}
                      </TableCell>

                      <TableCell>
                        {book.category_name ||
                          book.category?.name ||
                          book.category ||
                          '-'}
                      </TableCell>

                      <TableCell>
                        {book.publisher || '-'}
                      </TableCell>

                      <TableCell>
                        {book.shelf_no || '-'}
                      </TableCell>

                      <TableCell align="center">
                        {book.total_copies ?? 0}
                      </TableCell>

                      <TableCell align="center">
                        {book.available_copies ?? 0}
                      </TableCell>

                      <TableCell>
                        {book.status_display ||
                          book.status ||
                          '-'}
                      </TableCell>

                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                        >
                          <Tooltip
                            title={
                              book.is_active
                                ? 'Click to deactivate'
                                : 'Click to activate'
                            }
                          >
                            <span>
                              <Switch
                                size="small"
                                checked={Boolean(
                                  book.is_active
                                )}
                                onChange={() =>
                                  setStatusBook(book)
                                }
                              />
                            </span>
                          </Tooltip>

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: book.is_active
                                ? theme.palette
                                    .success.main
                                : theme.palette
                                    .error.main,
                            }}
                          >
                            {book.is_active
                              ? 'Active'
                              : 'Inactive'}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell align="center">
                        {/* EDIT */}

                        <IconButton
                          onClick={() =>
                            handleEdit(book)
                          }
                          title="Edit"
                          sx={{
                            color:
                              theme.palette
                                .primary.main,
                          }}
                        >
                          <EditIcon />
                        </IconButton>

                        {/* DELETE */}

                        {book.is_active && (
                          <IconButton
                            onClick={() =>
                              handleShowDeleteModal(
                                book
                              )
                            }
                            title="Delete"
                            sx={{
                              color:
                                theme.palette
                                  .error.main,
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                )
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={13}
                    align="center"
                    sx={{
                      py: 5,
                      color: textSecondary,
                    }}
                  >
                    No Books Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ===================================================
          PAGINATION
      =================================================== */}

      {sortedBooks.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: textPrimary }}
          >
            Page <strong>{page}</strong> of{' '}
            <strong>{totalPages}</strong>
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
          >
            <IconButton
              onClick={() =>
                handleChangePage(null, page - 1)
              }
              disabled={page === 1}
              sx={{ color: textPrimary }}
            >
              <ChevronLeft />
            </IconButton>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <IconButton
                key={pageNumber}
                onClick={() =>
                  handleChangePage(
                    null,
                    pageNumber
                  )
                }
                sx={{
                  width: 36,
                  height: 36,

                  backgroundColor:
                    page === pageNumber
                      ? theme.palette.primary.main
                      : 'transparent',

                  color:
                    page === pageNumber
                      ? theme.palette.primary
                          .contrastText
                      : textPrimary,

                  '&:hover': {
                    backgroundColor:
                      page === pageNumber
                        ? theme.palette.primary
                            .dark
                        : theme.palette.action
                            .hover,
                  },
                }}
              >
                {pageNumber}
              </IconButton>
            ))}

            <IconButton
              onClick={() =>
                handleChangePage(null, page + 1)
              }
              disabled={page >= totalPages}
              sx={{ color: textPrimary }}
            >
              <ChevronRight />
            </IconButton>

            <FormControl
              size="small"
              sx={{
                minWidth: 100,
                ml: 1,

                '& .MuiInputLabel-root': {
                  color: textPrimary,
                },

                '& .MuiInputLabel-root.Mui-focused':
                  {
                    color:
                      theme.palette.primary.main,
                  },
              }}
            >
              <InputLabel>Rows</InputLabel>

              <Select
                value={rowsPerPage}
                label="Rows"
                onChange={handleChangeRowsPerPage}
                sx={{
                  color: textPrimary,

                  '& .MuiSelect-select': {
                    color: textPrimary,
                  },

                  '& .MuiSvgIcon-root': {
                    color: textPrimary,
                  },

                  '& .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: isDark
                        ? 'rgba(255,255,255,0.30)'
                        : 'rgba(0,0,0,0.25)',
                    },
                }}
                MenuProps={menuProps}
              >
                {rowsPerPageOptions.map(
                  (option) => (
                    <MenuItem
                      key={option}
                      value={option}
                    >
                      {option}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* ===================================================
          ADD MODAL
      =================================================== */}

      <Modal
        open={showAddBookModal}
        onClose={() =>
          !saving && setShowAddBookModal(false)
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 600 },
            maxHeight: '90vh',
            bgcolor: paperBackground,
            color: textPrimary,
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme.palette.primary
                    .contrastText,
                fontWeight: 'bold',
              }}
            >
              Add Book
            </Typography>

            <IconButton
              onClick={() =>
                setShowAddBookModal(false)
              }
              sx={{
                color:
                  theme.palette.primary
                    .contrastText,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {renderBookForm(false)}
        </Box>
      </Modal>

      {/* ===================================================
          EDIT MODAL
      =================================================== */}

      <Modal
        open={showEditBookModal}
        onClose={() =>
          !saving && setShowEditBookModal(false)
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 600 },
            maxHeight: '90vh',
            bgcolor: paperBackground,
            color: textPrimary,
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color:
                    theme.palette.primary
                      .contrastText,
                  fontWeight: 'bold',
                }}
              >
                Edit Book
              </Typography>

              {selectedBook?.book_code && (
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      theme.palette.primary
                        .contrastText,
                    opacity: 0.8,
                  }}
                >
                  Book Code:{' '}
                  {selectedBook.book_code}
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={() =>
                setShowEditBookModal(false)
              }
              sx={{
                color:
                  theme.palette.primary
                    .contrastText,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {renderBookForm(true)}
        </Box>
      </Modal>

      {/* ===================================================
          DELETE MODAL
      =================================================== */}

      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxWidth: '90%',
            bgcolor: paperBackground,
            color: textPrimary,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.error.main,
              p: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 600,
              }}
            >
              Delete Confirmation
            </Typography>
          </Box>

          {/* CONTENT */}

          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                mb: 2,
                color: textPrimary,
              }}
            >
              Are you sure you want to delete{' '}
              <strong>
                {selectedBook?.title}
              </strong>
              ?
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: textSecondary,
              }}
            >
              This is a soft delete. The book
              will be marked as inactive and
              will not be permanently removed.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseDeleteModal}
                disabled={saving}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  'YES, DELETE'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ===================================================
          ACTIVATE / DEACTIVATE MODAL
      =================================================== */}

      <Modal
        open={Boolean(statusBook)}
        onClose={() =>
          !saving && setStatusBook(null)
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxWidth: '90%',
            bgcolor: paperBackground,
            color: textPrimary,
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2,
              backgroundColor: statusBook?.is_active
                ? theme.palette.error.main
                : theme.palette.success.main,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 600,
              }}
            >
              {statusBook?.is_active
                ? 'Deactivate'
                : 'Activate'}{' '}
              Confirmation
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography sx={{ mb: 3 }}>
              Are you sure you want to{' '}
              {statusBook?.is_active
                ? 'deactivate'
                : 'activate'}{' '}
              <strong>
                {statusBook?.title}
              </strong>
              ?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={() =>
                  setStatusBook(null)
                }
                disabled={saving}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                color={
                  statusBook?.is_active
                    ? 'error'
                    : 'success'
                }
                onClick={handleConfirmStatus}
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  'YES, CONFIRM'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ===================================================
          BULK ADD MODAL
      =================================================== */}

      <Modal
        open={showBulkModal}
        onClose={() =>
          !saving && setShowBulkModal(false)
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 900,
            maxWidth: '95%',
            maxHeight: '90vh',
            bgcolor: paperBackground,
            color: textPrimary,
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header (fixed) */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme.palette.primary
                    .contrastText,
                fontWeight: 'bold',
              }}
            >
              Bulk Add Books
            </Typography>

            <IconButton
              onClick={() =>
                !saving && setShowBulkModal(false)
              }
              sx={{
                color:
                  theme.palette.primary
                    .contrastText,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Body (scrollable) */}

          <CustomTableScrollbar
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: '28px 24px 24px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 2, color: textSecondary }}
            >
              Title + Author + Edition must be
              unique. Books that are already
              active are skipped, and matching
              inactive books are restored
              automatically.
            </Typography>

            {bulkRows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 600 }}
                  >
                    Book {index + 1}
                  </Typography>

                  <IconButton
                    color="error"
                    size="small"
                    onClick={() =>
                      removeBulkRow(index)
                    }
                    disabled={
                      bulkRows.length === 1
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(3, 1fr)',
                    },
                    gap: 2,
                  }}
                >
                  {BULK_FIELDS.map((f) =>
                    f.select ? (
                      <Autocomplete
                        key={f.key}
                        fullWidth
                        size="small"
                        options={categoryOptions}
                        value={
                          categoryOptions.find(
                            (option) =>
                              String(option.value) ===
                              String(row[f.key])
                          ) || null
                        }
                        onChange={(_, option) =>
                          updateBulkRow(
                            index,
                            f.key,
                            option?.value ?? ''
                          )
                        }
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) =>
                          String(option.value) === String(value.value)
                        }
                        noOptionsText="No categories found"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={f.label}
                            placeholder="Search categories..."
                            sx={{
                              ...commonInputSx,
                              mb: 0,
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        key={f.key}
                        label={f.label}
                        size="small"
                        type={f.type || 'text'}
                        value={row[f.key]}
                        disabled={Boolean(f.disabled)}
                        onChange={(e) =>
                          updateBulkRow(
                            index,
                            f.key,
                            e.target.value
                          )
                        }
                        inputProps={{
                          maxLength: f.max,
                          inputMode: f.key === 'price' ? 'decimal' : undefined,
                          min:
                            f.type === 'number'
                              ? 0
                              : undefined,
                        }}
                        sx={{
                          ...commonInputSx,
                          mb: 0,
                        }}
                      />
                    )
                  )}
                </Box>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={addBulkRow}
              sx={{ color: texterror }}
            >
              Add another book
            </Button>
          </CustomTableScrollbar>

          {/* Footer (fixed) */}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 2,
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: paperBackground,
            }}
          >
            <Button
              variant="contained"
              onClick={handleBulkSave}
              disabled={
                saving || !selectedBranchId
              }
            >
              {saving ? (
                <CircularProgress
                  size={22}
                  color="inherit"
                />
              ) : (
                'SAVE ALL'
              )}
            </Button>

            <Button
              variant="contained"
              onClick={() =>
                setShowBulkModal(false)
              }
              disabled={saving}
            >
              CANCEL
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ===================================================
          USER GUIDE
      =================================================== */}

      <Modal
        open={showUserGuideModal}
        onClose={() =>
          setShowUserGuideModal(false)
        }
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', md: 850 },
            maxHeight: '90vh',
            bgcolor: paperBackground,
            color: textPrimary,
            borderRadius: 3,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color:
                  theme.palette.primary
                    .contrastText,
                fontWeight: 'bold',
              }}
            >
              Book Master - User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(false)
              }
              sx={{
                color:
                  theme.palette.primary
                    .contrastText,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{
              maxHeight: 'calc(90vh - 80px)',
              p: 3,
              color: textPrimary,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                mb: 1,
              }}
            >
              ➤ Add a Book
            </Typography>

            <Box
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: textPrimary,
              }}
            >
              <li>
                Click <strong>ADD BOOK</strong>.
              </li>
              <li>
                Enter all required
                information.
              </li>
              <li>
                Organization and selected
                Branch are automatically
                sent to the API.
              </li>
              <li>
                Category is loaded from the
                Book Category API.
              </li>
              <li>
                Course and Batch are loaded
                according to the selected
                branch. Batch is enabled
                only after selecting a
                course.
              </li>
              <li>
                Click <strong>SAVE</strong>.
              </li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Bulk Add Books
            </Typography>

            <Box
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: textPrimary,
              }}
            >
              <li>
                Click <strong>BULK ADD</strong>.
              </li>
              <li>
                Fill in multiple books. Title,
                Author and Category are
                required per row.
              </li>
              <li>
                Title + Author + Edition must be
                unique within one request.
              </li>
              <li>
                Already-active books are
                skipped; matching inactive
                books are restored.
              </li>
              <li>
                Click <strong>SAVE ALL</strong>.
              </li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Branch Selection
            </Typography>

            <Typography
              sx={{ color: textPrimary }}
            >
              Branch is selected globally from
              the navbar. All lists, filters
              and forms on this page follow
              that selection automatically.
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Edit a Book
            </Typography>

            <Box
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: textPrimary,
              }}
            >
              <li>Click the Edit icon.</li>
              <li>
                Update the required
                information.
              </li>
              <li>
                Click <strong>UPDATE</strong>.
              </li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Activate / Deactivate a Book
            </Typography>

            <Box
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: textPrimary,
              }}
            >
              <li>
                Use the Switch in the Status
                column.
              </li>
              <li>
                Confirmation modal will
                appear.
              </li>
              <li>
                Click <strong>YES, CONFIRM</strong>.
              </li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Delete a Book
            </Typography>

            <Box
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: textPrimary,
              }}
            >
              <li>
                Click the Delete (trash) icon
                on an active book.
              </li>
              <li>
                This performs a soft delete
                via the API.
              </li>
              <li>
                Click <strong>YES, DELETE</strong>.
              </li>
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: theme.palette.primary.main,
                mt: 3,
                mb: 1,
              }}
            >
              ➤ Search & Filters
            </Typography>

            <Box
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: textPrimary,
              }}
            >
              <li>
                Search by Book Code, ISBN,
                Title or Author. Search is
                debounced and sent to the
                API using the{' '}
                <code>search</code> parameter.
              </li>
              <li>
                Filter by Status (Active /
                Inactive), Condition
                (Active, Inactive, Lost,
                Damaged) and Category.
              </li>
              <li>
                Click column headers (Book
                Code, Title, Author) to sort.
              </li>
            </Box>
          </CustomTableScrollbar>
        </Box>
      </Modal>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setOpenSnackbar(false)}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: 600,
            fontWeight: 600,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default BookMaster;